import { useEffect, useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import pr from './PersonRecordPanel.module.css'
import styles from './BulkEditPanel.module.css'
import { useAuth } from '../../contexts/AuthContext'
import { useLayer } from '../../components/layers'
import { PendingButton, inertClass, useSlow, Failed } from '../../components/pending'
import { apiJson } from '../../lib/api'
import { can, hasPower, useAccess } from '../../lib/access'
import { dmy, money, plural } from '../wages/format'
import { todayLocal } from '../timesheets/time'
import { fullName, initialsOf, type Person } from './PersonRecordPanel'
import { DateField } from '../../components/DateField'

// Bulk edit (handoff §7b 859-928): a right slide-over over two or more ticked
// records. One person opens their own record instead, so the words "bulk
// edit" are never over a single person. Every field starts "Unchanged" and
// only touched fields are sent; the server half is POST /api/personnel/bulk.

interface GroupOpt {
  id: string
  name: string
  is_default: boolean
  is_archived: boolean
}
interface TeamOpt {
  id: string
  name: string
  active: boolean
  is_induction: boolean
}
export type Tri = 'unchanged' | 'on' | 'off'
type GroupPick = { kind: 'unchanged' } | { kind: 'existing'; id: string; name: string } | { kind: 'new'; name: string }
interface BulkResult {
  updated: number
  fields: string[]
  rate_versions: number
  group: { id: string; name: string; created: boolean } | null
  queued: number
  locked_unchanged: string[]
  // Archiving removes CONTROL sign-in in the same step (C-1(a)).
  sign_in_removed?: number
}

// The group register's own rule (§7c, groups.js nameKey): "A1 Fresh Packers"
// and "a1-fresh packers" are one name.
export const nameKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')
const GROUP_CAP = 5
const CHIP_CAP = 8
const REPLACING_CAP = 6

function parseRate(v: string): number | null {
  const n = Number(v.replace(/[$,\s]/g, ''))
  return v.trim() && Number.isFinite(n) && n > 0 && n < 100000 ? Math.round(n * 100) / 100 : null
}

// A field worker with no rate at all: the server refuses any change for them
// that does not set one (the record panel's rule).
const needsRate = (p: Person) => !p.membership_id && p.hourly_rate == null && p.next_rate == null

export function TriState({ label, value, options, onChange }: { label: string; value: Tri; options: [string, string]; onChange: (v: Tri) => void }) {
  const opts: [Tri, string][] = [['unchanged', 'Unchanged'], ['on', options[0]], ['off', options[1]]]
  return (
    <div className={styles.tri} role="radiogroup" aria-label={label}>
      {opts.map(([v, text]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} className={value === v ? styles.triOn : styles.triOpt} onClick={() => onChange(v)}>
          {text}
        </button>
      ))}
    </div>
  )
}

interface Props {
  people: Person[]
  onClose: () => void
  onSaved: (message: string) => void
}

export function BulkEditPanel({ people, onClose, onSaved }: Props) {
  const { user } = useAuth()
  // Without pay_data the people arrive with no rates (approved data
  // boundary, D1 point 7): no rate change is offered, and whether someone
  // lacks one is the server's to say. Archive needs Personnel Add.
  const mine = useAccess()
  const pay = hasPower(mine, 'pay_data')
  const mayArchive = can(mine, 'personnel', 'add')
  const n = people.length
  const today = todayLocal()
  const [groups, setGroups] = useState<GroupOpt[] | null>(null)
  const [teams, setTeams] = useState<TeamOpt[] | null>(null)
  const [roleOptions, setRoleOptions] = useState<string[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)

  const [rate, setRate] = useState('')
  const [rateFrom, setRateFrom] = useState(today)
  const [group, setGroup] = useState<GroupPick>({ kind: 'unchanged' })
  const [groupQ, setGroupQ] = useState('')
  const [groupOpen, setGroupOpen] = useState(false)
  const [teamId, setTeamId] = useState('unchanged')
  const [addRoles, setAddRoles] = useState<string[]>([])
  const [exclude, setExclude] = useState<Tri>('unchanged')
  const [archive, setArchive] = useState<Tri>('unchanged')
  const [sent, setSent] = useState<{ from: string; days_back: number; sent_count: number; sent_people: number } | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    const fail = (e: any) => live && setLoadError(e?.message || 'Could not load the groups, teams and roles')
    apiJson<GroupOpt[]>(user, '/api/groups').then((r) => live && setGroups(Array.isArray(r) ? r : [])).catch(fail)
    apiJson<TeamOpt[]>(user, '/api/teams').then((r) => live && setTeams(Array.isArray(r) ? r : [])).catch(fail)
    apiJson<{ roles: string[] }>(user, '/api/personnel/roles').then((r) => live && setRoleOptions(r?.roles || [])).catch(fail)
    return () => {
      live = false
    }
  }, [])

  const rateValue = parseRate(rate)
  const rateTouched = rate.trim() !== ''
  const backdated = rateValue !== null && rateFrom < today
  const idKey = people.map((p) => p.id).join(',')

  // The backdate notice counts the whole selection's days already sent to
  // payroll, from the chosen date; never a fixed number.
  useEffect(() => {
    setSent(null)
    if (!backdated || !/^\d{4}-\d{2}-\d{2}$/.test(rateFrom)) return
    let live = true
    apiJson<{ from: string; days_back: number; sent_count: number; sent_people: number }>(user, '/api/personnel/bulk/sent-since', {
      body: { ids: people.map((p) => p.id), from: rateFrom },
    })
      .then((r) => live && setSent(r))
      .catch(() => live && setSent(null))
    return () => {
      live = false
    }
  }, [backdated, rateFrom, idKey])

  const changes: string[] = []
  if (rateValue !== null) changes.push('hourly rate')
  if (group.kind !== 'unchanged') changes.push('group')
  if (teamId !== 'unchanged') changes.push('default team')
  if (addRoles.length) changes.push('job roles')
  if (exclude !== 'unchanged') changes.push('wages')
  if (archive !== 'unchanged') changes.push(archive === 'on' ? 'archive' : 'bring back')
  const dirty = changes.length > 0 || rateTouched

  const noRate = pay ? people.filter(needsRate) : []
  const missing: string[] = []
  if (rateTouched && rateValue === null) missing.push('a valid hourly rate')
  if (changes.length && noRate.length && rateValue === null) missing.push('an hourly rate')
  const blocked = missing.length > 0

  // The panel is a layer (h 425-458), and so is the group list while it shows:
  // Escape closes the list first. Nothing closes while the change is applying.
  const panelEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(
    true,
    () => {
      if (!busy) onClose()
    },
    { dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: true, busy }
  )
  const slow = useSlow(busy)
  useLayer(groupOpen, () => setGroupOpen(false))

  // Group: nothing listed until something is typed; then up to five live
  // groups containing the text, with "Create group X" first unless the name
  // is already taken (matched the way the register matches).
  const live = (groups || []).filter((x) => !x.is_archived)
  const needle = groupQ.trim()
  const matches = needle ? live.filter((x) => x.name.toLowerCase().includes(needle.toLowerCase())) : []
  const shownGroups = matches.slice(0, GROUP_CAP)
  const taken = needle ? (groups || []).find((x) => nameKey(x.name) === nameKey(needle)) : undefined
  const canCreate = !!needle && !!nameKey(needle) && !taken && needle.length <= 100
  const groupLabel = group.kind === 'existing' ? group.name : group.kind === 'new' ? `${group.name} (new group)` : ''
  const pickGroup = (p: GroupPick) => {
    setGroup(p)
    setGroupQ('')
    setGroupOpen(false)
  }

  // Default team picks from the Teams register only: active, not induction.
  // A team someone selected already has is kept on the list, unless it is an
  // induction team, which is never offered.
  const stored = new Set(people.map((p) => p.default_team_id).filter(Boolean) as string[])
  const teamOpts = (teams || []).filter((t) => !t.is_induction && (t.active || stored.has(t.id)))

  const replacing = rateValue === null ? [] : people.slice(0, REPLACING_CAP)

  const save = async () => {
    if (blocked || busy || !changes.length) return
    setBusy(true)
    setSaveError(null)
    const body: Record<string, unknown> = { ids: people.map((p) => p.id) }
    if (rateValue !== null) {
      body.hourlyRate = rateValue
      body.hourlyRateEffectiveFrom = rateFrom
    }
    if (group.kind === 'existing') body.groupId = group.id
    if (group.kind === 'new') body.newGroupName = group.name
    if (teamId !== 'unchanged') body.defaultTeamId = teamId === 'none' ? null : teamId
    if (addRoles.length) body.addRoles = addRoles
    if (exclude !== 'unchanged') body.excludeFromWages = exclude === 'on'
    if (archive !== 'unchanged') body.archived = archive === 'on'
    try {
      const r = await apiJson<BulkResult>(user, '/api/personnel/bulk', { body })
      const parts = [`${plural(r.updated, 'record', 'records')} updated: ${changes.join(', ')}`]
      if (r.group?.created) parts.push(`group ${r.group.name} created`)
      if (r.queued) parts.push(`${plural(r.queued, 'worker/day', 'worker/days')} queued for recalculation`)
      if (r.locked_unchanged?.length) parts.push(`${plural(r.locked_unchanged.length, 'locked day keeps', 'locked days keep')} their locked pay`)
      if (r.sign_in_removed) parts.push(`CONTROL sign-in removed for ${plural(r.sign_in_removed, 'person', 'people')}`)
      onSaved(parts.join(' · '))
    } catch (e: any) {
      setSaveError(e?.message || 'Nothing was changed')
      setBusy(false)
    }
  }

  return (
    <div className={o.drawerScrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={panelEl} tabIndex={-1} className={`${o.drawer} ${styles.drawer}`} role="dialog" aria-label={`Edit ${n} records`}>
        <div className={o.drawerHead}>
          <div className={o.headText}>
            <h2 className={o.drawerTitle}>Edit {n} records</h2>
            <p className={o.drawerSub}>Anything you leave alone stays as it is on each of the {n} records.</p>
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={styles.chips} aria-label="Selected people" data-testid="bulk-chips">
            {people.slice(0, CHIP_CAP).map((p) => (
              <span key={p.id} className={styles.chip}>
                <span className={styles.chipAvatar} aria-hidden="true">
                  {initialsOf(p)}
                </span>
                {fullName(p)}
              </span>
            ))}
            {n > CHIP_CAP && <span className={styles.more}>and {n - CHIP_CAP} more</span>}
        </div>

        <fieldset className={`${o.drawerBody} ${styles.body} ${inertClass(busy, slow)}`} disabled={busy} aria-busy={busy}>
          {loadError && <div className={pr.reason}>{loadError}</div>}

          {pay && (
          <div className={o.section} role="group" aria-label="Rate change">
            <div className={o.grid2}>
              <div className={o.field}>
                <label className={o.label} htmlFor="be-rate">
                  Hourly rate
                </label>
                <input id="be-rate" inputMode="decimal" className={rateTouched && rateValue === null ? `${o.input} ${o.inputBad}` : o.input} value={rate} placeholder="Unchanged" onChange={(e) => setRate(e.target.value)} />
              </div>
              <div className={o.field}>
                <label className={o.label} htmlFor="be-from">
                  Effective from
                </label>
                <DateField id="be-from" className={o.input} value={rateFrom} onChange={(e) => setRateFrom(e.target.value || today)} />
              </div>
            </div>
            {rateTouched && rateValue === null && <span className={pr.needed}>Enter a rate per hour above $0, or clear it to leave rates unchanged.</span>}
            <span className={o.fieldHint}>Written as a separate new version on each person from this date. Nobody's history is merged or overwritten.</span>
            {rateValue !== null && (
              <div className={styles.replacing} data-testid="replacing">
                <span className={styles.repHead}>Replacing</span>
                {replacing.map((p) => (
                  <div key={p.id} className={styles.repRow}>
                    <span className={styles.repName}>{fullName(p)}</span>
                    {p.hourly_rate != null ? <s className={styles.repOld}>{money(Number(p.hourly_rate))}/h</s> : <span className={styles.repNone}>No rate yet</span>}
                    <span aria-hidden="true">→</span>
                    <span className={styles.repNew}>{money(rateValue)}/h</span>
                    {p.hourly_rate == null && <span className={styles.repNone}>first rate</span>}
                  </div>
                ))}
                {n > REPLACING_CAP && <span className={o.fieldHint}>and {n - REPLACING_CAP} more, each from their own current rate</span>}
              </div>
            )}
            {backdated && sent && sent.sent_count > 0 && (
              <div className={`${o.warn} ${pr.block}`} data-testid="backdate-notice">
                This rate reaches back {plural(sent.days_back, 'day', 'days')}, to {dmy(rateFrom)}. {plural(sent.sent_count, 'day', 'days')} across{' '}
                {sent.sent_people === n ? `all ${n} people selected` : `${sent.sent_people} of the ${n} people selected`} in that stretch{' '}
                {sent.sent_count === 1 ? 'was' : 'were'} already sent to payroll. Check the payroll system for what {sent.sent_count === 1 ? 'that day was' : 'those days were'} paid at: the
                payroll system is the authority on what was paid, and CONTROL does not calculate a difference for {sent.sent_count === 1 ? 'it' : 'them'}.
              </div>
            )}
            {noRate.length > 0 && rateValue === null && changes.length > 0 && (
              <div className={pr.reason} data-testid="needs-rate">
                {noRate.length === n
                  ? `None of the ${n} people selected has an hourly rate yet.`
                  : `${noRate.map(fullName).slice(0, 3).join(', ')}${noRate.length > 3 ? ` and ${noRate.length - 3} others` : ''} ${noRate.length === 1 ? 'has' : 'have'} no hourly rate yet.`}{' '}
                A worker with no rate cannot be paid correctly, so set one in this change.
              </div>
            )}
          </div>
          )}

          <div className={`${o.field} ${styles.comboField}`}>
            <label className={o.label} htmlFor="be-group">
              Group
            </label>
            <input
              id="be-group"
              role="combobox"
              aria-expanded={groupOpen && !!needle}
              aria-controls="be-group-list"
              aria-autocomplete="list"
              autoComplete="off"
              className={o.input}
              disabled={!groups}
              value={groupOpen ? groupQ : groupLabel}
              placeholder={groups ? 'Unchanged' : 'Loading…'}
              onFocus={() => {
                setGroupOpen(true)
                setGroupQ('')
              }}
              onChange={(e) => {
                setGroupOpen(true)
                setGroupQ(e.target.value)
              }}
              onBlur={() => setGroupOpen(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && groupOpen && needle) {
                  e.preventDefault()
                  if (canCreate) pickGroup({ kind: 'new', name: needle })
                  else if (shownGroups[0]) pickGroup({ kind: 'existing', id: shownGroups[0].id, name: shownGroups[0].name })
                }
              }}
            />
            <span className={o.fieldHint}>
              Type to find a group or to create one. A group created here is added for the whole workspace.{' '}
              {group.kind !== 'unchanged' && (
                <button type="button" className={pr.linkBtn} onClick={() => pickGroup({ kind: 'unchanged' })}>
                  Leave groups unchanged
                </button>
              )}
            </span>
            {groupOpen && needle && (
              <ul id="be-group-list" role="listbox" aria-label="Group" className={styles.options}>
                {canCreate && (
                  <li
                    role="option"
                    aria-selected={false}
                    className={styles.optCreate}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      pickGroup({ kind: 'new', name: needle })
                    }}
                  >
                    <span className={styles.optName}>Create group “{needle}”</span>
                    <span className={styles.optMail}>Adds it for the whole workspace</span>
                  </li>
                )}
                {shownGroups.map((x) => (
                  <li
                    key={x.id}
                    role="option"
                    aria-selected={group.kind === 'existing' && group.id === x.id}
                    className={group.kind === 'existing' && group.id === x.id ? styles.optionOn : styles.option}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      pickGroup({ kind: 'existing', id: x.id, name: x.name })
                    }}
                  >
                    <span className={styles.optName}>{x.name}</span>
                  </li>
                ))}
                {taken?.is_archived && <li className={styles.optNone}>{taken.name} is archived. Bring it back from Groups to use it.</li>}
                {!canCreate && !taken && shownGroups.length === 0 && <li className={styles.optNone}>No group matches</li>}
                {shownGroups.length > 0 && shownGroups.length < live.length && (
                  <li className={styles.optNone} data-testid="group-count">
                    {shownGroups.length} of {live.length} groups shown — keep typing to narrow
                  </li>
                )}
              </ul>
            )}
          </div>

          <div className={o.field}>
            <label className={o.label} htmlFor="be-team">
              Default team
            </label>
            <select id="be-team" className={o.input} value={teamId} disabled={!teams} onChange={(e) => setTeamId(e.target.value)}>
              <option value="unchanged">Unchanged</option>
              <option value="none">No default team</option>
              {teamOpts.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {!t.active ? ' (not active)' : ''}
                </option>
              ))}
            </select>
            <span className={o.fieldHint}>Picks from Teams only, because a team has a roster and settings behind it. Only used by the Preassign station and scheduled sign-ons.</span>
          </div>

          <div className={o.field} role="group" aria-label="Job roles to add">
            <span className={o.label}>Job roles to add</span>
            <div className={pr.roles}>
              {roleOptions.map((r) => {
                const on = addRoles.includes(r)
                return (
                  <button key={r} type="button" aria-pressed={on} className={on ? pr.roleOn : pr.role} onClick={() => setAddRoles(on ? addRoles.filter((x) => x !== r) : [...addRoles, r])}>
                    {r}
                  </button>
                )
              })}
            </div>
            <span className={o.fieldHint}>Added to each person's roles; the roles they have are kept. Labels for finding people ("show me my pickers"): they grant nothing, and app access comes from the team's clock-on QR.</span>
          </div>

          <div className={`${o.toggleRow} ${styles.triRow}`}>
            <div className={o.toggleText}>
              <div className={o.toggleTitle}>Exclude from wages</div>
              <div className={o.toggleSub}>
                {exclude === 'on'
                  ? `All ${n} are left out of pay and payroll exports. Their timesheets stay recorded. Locked days keep their locked pay; to change one, unlock that day on Timesheets first.`
                  : exclude === 'off'
                    ? `All ${n} are included in pay again, and their recorded days are recalculated. Locked days keep their locked pay; to change one, unlock that day on Timesheets first.`
                    : 'Each person keeps their own setting.'}
              </div>
            </div>
            <TriState label="Exclude from wages" value={exclude} options={['Exclude', 'Include']} onChange={setExclude} />
          </div>

          {mayArchive && (
          <div className={`${o.toggleRow} ${styles.triRow}`}>
            <div className={o.toggleText}>
              <div className={o.toggleTitle}>Archive</div>
              <div className={o.toggleSub}>
                {archive === 'on'
                  ? `All ${n} move to the Archived list. Nothing is deleted, so their history stays intact.${
                      people.some((p) => p.membership_id && !p.archived)
                        ? hasPower(mine, 'manage_sign_in')
                          ? ' Anyone here who signs in to CONTROL loses that sign-in here; bringing them back does not give it back.'
                          : ' Someone here signs in to CONTROL, so only an ADMIN can archive them.'
                        : ''
                    }`
                  : archive === 'off'
                    ? `All ${n} come back from the Archived list.`
                    : 'Each person stays where they are.'}
              </div>
            </div>
            <TriState label="Archive" value={archive} options={['Archive', 'Bring back']} onChange={setArchive} />
          </div>
          )}
        </fieldset>

        <div className={`${o.drawerFoot} ${pr.foot}`}>
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            Cancel
          </button>
          <span className={blocked ? o.footNote : `${o.footNote} ${styles.changeNote}`} data-testid="bulk-changes">
            {blocked ? `Still needed: ${missing.join(', ')}` : changes.length ? `Changes: ${changes.join(', ')}` : 'Nothing has changed yet'}
          </span>
          <PendingButton className={o.primary} busy={busy} pending="Applying…" disabled={blocked || !changes.length} onClick={save}>
            {`Apply to ${n}`}
          </PendingButton>
          <Failed text={saveError && `${saveError} Nobody was changed.`} />
        </div>
      </div>
      {confirm}
    </div>
  )
}
